import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { MapPin, Plus, Building2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { listMyVenues } from "@/lib/venues.functions";
import { getAuthHeaders } from "@/lib/auth-helper";
import { AccessGate } from "@/components/permissions/AccessGate";

type Venue = {
  id: string;
  name: string;
  city: string;
  address: string | null;
  is_reusable: boolean;
  created_at: string;
  venue_maps: { id: string; width: number; height: number; background_image_url: string | null }[];
};

export const Route = createFileRoute("/dashboard/venues/")({
  head: () => ({
    meta: [{ title: "Venues — ZYNTRA" }, { name: "description", content: "Gestioná las locaciones de tus eventos." }],
  }),
  component: VenuesGuarded,
});

function VenuesGuarded() {
  return (
    <AccessGate required="manage-venues">
      <VenuesPage />
    </AccessGate>
  );
}

function VenuesPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loadingVenues, setLoadingVenues] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate({ to: "/login" });
      return;
    }
    if (user) loadVenues();
  }, [user, loading]);

  async function loadVenues() {
    setLoadingVenues(true);
    try {
      const headers = await getAuthHeaders();
      const result = await listMyVenues({ headers } as any);
      setVenues((result.venues || []) as any);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingVenues(false);
    }
  }

  if (loading || loadingVenues) {
    return <div className="mx-auto max-w-5xl px-4 pt-24 pb-20 text-muted-foreground">Cargando...</div>;
  }

  return (
    <div className="mx-auto max-w-5xl px-4 pt-24 pb-20 sm:px-6">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Volver al dashboard
      </Link>

      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground">Venues</h1>
          <p className="mt-1 text-sm text-muted-foreground">Locaciones reutilizables con sus zonas configuradas.</p>
        </div>
        <Link
          to="/dashboard/venues/new"
          className="inline-flex items-center justify-center gap-2 rounded-xl gradient-primary px-4 py-2.5 text-sm font-bold text-primary-foreground glow-primary"
        >
          <Plus className="h-4 w-4" />
          Crear venue
        </Link>
      </div>

      {venues.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center">
          <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-bold text-card-foreground">Aún no creaste ningún venue</h3>
          <p className="mt-1 text-sm text-muted-foreground">Definí una locación con sus zonas y reusala en cualquier evento.</p>
          <Link
            to="/dashboard/venues/new"
            className="mt-5 inline-flex items-center gap-2 rounded-xl gradient-primary px-4 py-2.5 text-sm font-bold text-primary-foreground"
          >
            <Plus className="h-4 w-4" /> Crear el primero
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {venues.map((v) => (
            <Link
              key={v.id}
              to="/dashboard/venues/$venueId"
              params={{ venueId: v.id }}
              className="group rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary/50 hover:bg-accent/30"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-bold text-card-foreground group-hover:text-primary">{v.name}</h3>
                  <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    {v.city}
                    {v.address ? ` · ${v.address}` : ""}
                  </p>
                </div>
                {v.is_reusable && (
                  <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">Reutilizable</span>
                )}
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Mapa {v.venue_maps?.[0]?.width}×{v.venue_maps?.[0]?.height}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
